import { Capacitor } from "@capacitor/core";
import { CornerDownRight, X } from 'lucide-react';
import { Keyboard } from '@capacitor/keyboard';
import { Trans, msg, t } from "@lingui/macro";
import { RecordingData } from "capacitor-voice-recorder";
import EventEmitter, { on } from "events";
import { memo, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { SuggestionDataItem } from "react-mentions";
import { FileAttachment, Message, SendFile } from "../../../models/Message";
import { Room } from "../../../models/Room";
import { Soribot } from "../../../models/Soribot";
import { EmojiType } from "../../../models/local/Types";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { ResizeImage } from "../../../services/Utils/ImageResizer";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { getDisplayName, resolveMessageUser } from "../../../services/Utils/userDisplayName";
import { RoomRefreshEmitter } from "../../rooms/RoomEvents";
import { AppSettings } from "../../../const/Settings";
import MessageTextArea from "../MessageTextArea";
import { PlayMessageButton } from "../PlayMessageButton";
import ProfileImg from "../ProfileImg";
import { UIMessage } from "../UIMessage";
import MsgAddMorePopover, { UploadFilesContext } from "./MsgAddMorePopover";
import MsgEmojiSelector from "./MsgEmojiSelector";
import MsgInputFooterReactions from "./MsgInputFooterReactions";
import MsgRecordModal, { RecordingAction } from "./MsgRecordModal";
import { RoomItem } from "../../../models/RoomItem";
import InputAddedItemList from "./InputAddedItemList";
import { Util } from "../../../services/Utils/Util";
import { useAppUIContext } from "../../../context/AppUIContext";
import { FileIcon } from "../MessageFileItem";
import { UploadTask } from "firebase/storage";
import { Avatar, AvatarImage, AvatarFallback } from "../../../components/ui/avatar";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Progress } from "../../../components/ui/progress";
import { useToast } from "../../../components/ui/use-toast";
import { cn } from "../../../lib/utils";
import { IconPlus, IconMoodSmile, IconMicrophone, IconSend } from '../FigmaIcons';

const MsgInputFooterEmitter = new EventEmitter();
interface IMsgInputFooter { room: Room, parentMessage: UIMessage | null, messages: UIMessage[], onClearParent: (arg0?: UIMessage) => void, onScrollToParent?: () => void, onProgressForFile: (key: string, sendFile: SendFile) => void, onSendedMessage: (arg0: UIMessage) => void, soribotsRef: React.RefObject<Soribot[]> };

// 🔧 전송 버튼: DOM ref 직접 조작으로 React 리렌더 완전 제거 (zero-state)
const SEND_ACTIVE_CLS = "bg-primary hover:bg-primary/90 opacity-100 text-primary-foreground";
const SEND_INACTIVE_CLS = "bg-muted opacity-70 cursor-not-allowed";
const SendButton = memo(({ emitter, onSubmit }: { emitter: EventEmitter, onSubmit: () => void }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const activeRef = useRef(false);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;



  useEffect(() => {
    const handler = (v: boolean) => {
      if (v === activeRef.current) return;
      activeRef.current = v;
      const el = btnRef.current;
      if (!el) return;
      // active 클래스 토글
      SEND_INACTIVE_CLS.split(' ').forEach(c => el.classList.toggle(c, !v));
      SEND_ACTIVE_CLS.split(' ').forEach(c => el.classList.toggle(c, v));
    };
    emitter.on('sendActive', handler);
    return () => { emitter.off('sendActive', handler); };
  }, [emitter]);

  const handleClick = useCallback(() => {
    if (activeRef.current) onSubmitRef.current();
  }, []);

  return (
    <Button
      ref={btnRef}
      variant="icon-outline"
      className={SEND_INACTIVE_CLS}
      onClick={handleClick}
    >
      <IconSend />
    </Button>
  );
});

const MsgInputFooter: React.FC<IMsgInputFooter> = memo(({ room, parentMessage, messages, onClearParent, onScrollToParent, onProgressForFile, onSendedMessage, soribotsRef }) => {
  // 🔧 useAppUIContext() 제거: Context 변경(알림 클리어 등) 시 불필요한 리렌더 방지
  // soribots는 submit()/getMentionSuggestions()에서만 사용하므로 ref로 충분
  const hasTextRef = useRef(false);
  // 🔧 sendActive를 state에서 제거 → ref + event emitter로 SendButton에만 전달
  const sendActiveRef = useRef(false);
  const sendActiveEmitter = useRef(new EventEmitter()).current;
  const isMentioningRef = useRef(false);
  const [inProgress, setInProgress] = useState(false);
  const [showEmojiSelctor, setShowEmojiSelector] = useState(false);
  const { toast } = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<RoomItem[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestionDataItem[]>([]);
  const [message, setMessage] = useState(new Message(room, My.user));
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [notifyOnlyMentioned, setNotifyOnlyMentioned] = useState(false);
  const [isMentioning, setIsMentioning] = useState(false);
  const [progress, setProgress] = useState<{ [key: string]: number }>({});
  const [uploadTask, setUploadTask] = useState<{ [key: string]: UploadTask }>({});
  const [isDragging, setIsDragging] = useState(false);



  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      Keyboard.addListener('keyboardDidShow', () => {
        setIsKeyboardOpen(true);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyboardOpen(false);
      });
    }

    MsgInputFooterEmitter.on('paste', (file) => {
      setFiles(p => p.concat(file));
    });
    return () => {
      MsgInputFooterEmitter.removeAllListeners();
      if (Capacitor.isNativePlatform()) {
        Keyboard.removeAllListeners();
      }
    };
  }, [])

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles: File[] = [];
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        const file = e.dataTransfer.files[i];
        if (!files.find(f => f.name === file.name)) {
          newFiles.push(file);
        }
      }
      if (newFiles.length > 0) {
        setFiles([...newFiles, ...files]);
      }
    }
  }

  useEffect(() => {
    setMessage(new Message(room, My.user));
    setNotifyOnlyMentioned(false);
    hasTextRef.current = false;
    sendActiveRef.current = false;
    sendActiveEmitter.emit('sendActive', false);
    setIsMentioning(false);
    setSuggestions(getMentionSuggestions())
  }, [room.id]);

  useEffect(() => {
    setSuggestions(getMentionSuggestions())
  }, [room.id, room.users.length]);

  useEffect(() => {
    message.replyTo = parentMessage?.msg.user;
    message.replyToMessageId = parentMessage?.msg.id ?? null;
    message.parentMessageId = parentMessage?.msg.parentMessageId ?? parentMessage?.msg.id ?? null;
    forceUpdate();
  }, [parentMessage]);

  return (
    <UploadFilesContext.Provider value={{ files, setFiles, items, setItems }}>
      <div
        id="msg-input-footer-container"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn("bg-background md:rounded-b-panel transition-all z-20 relative flex flex-col items-start w-full relative", isDragging && "relative")}
        style={isDragging ? { cursor: 'copy' } : {}}
      >
        {isDragging && (
          <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary flex items-center justify-center text-xl text-primary z-[1000] pointer-events-none rounded-b-panel">
            <Trans>Drop files here to attach</Trans>
          </div>
        )}
        {inProgress && <Progress value={undefined} className="absolute top-0 left-0 h-0.5 w-full z-30 mb-2" />}

        {/* Top Section */}
        <div className="flex flex-col items-start pb-4 pt-4 px-4 relative w-full gap-2 empty:hidden">
          {/* 답글 대상 메시지 표시 - Figma: bg-[var(--sub\/50,#fffaec)] p-2 rounded-lg */}
          {parentMessage && (
            <div className="bg-msg-selected-bg flex gap-2 items-start p-2 relative rounded-lg w-full min-w-0 overflow-hidden mb-1">
              <div
                className="flex-1 min-w-0 flex flex-col gap-1 items-start cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => onScrollToParent?.()}
              >
                <p className="font-semibold text-label text-muted-foreground leading-[16px] truncate w-full m-0">
                  <Trans>Replying to {getDisplayName(resolveMessageUser(parentMessage.msg.user, room.users))}</Trans>
                </p>
                <p
                  className="font-normal text-body text-[color:rgb(var(--text-body))] leading-[20px] truncate w-full m-0"
                  dangerouslySetInnerHTML={{ __html: parentMessage.msg.text ? UtilApp.getTextAsHTML(parentMessage.msg.text, My.user.id) : t`Attachment` }}
                />
              </div>
              <div
                className="flex items-center justify-center rounded-md w-7 h-7 shrink-0 cursor-pointer hover:bg-primary/15"
                onClick={() => onClearParent()}
              >
                <X className="h-[18px] w-[18px] text-muted-foreground" />
              </div>
            </div>
          )}

          {ShowEmojis()}
          {ShowRecording()}
          {files.length > 0 && <div className="flex gap-2.5 overflow-x-auto py-2 custom-scrollbar w-full">{files.map((file, i) => getFileItem(file, i))}</div>}
          <InputAddedItemList />

          {/* 텍스트 입력 영역 - Figma: MessageTextArea */}
          <div className="flex gap-2 items-center relative w-full min-h-5">
            {parentMessage && (
              <div className="shrink-0 w-4 h-4 text-muted-foreground opacity-60 self-start mt-2">
                <CornerDownRight className="w-full h-full" />
              </div>
            )}
            <div className="flex-1 min-w-0 bg-muted/30 border border-border/50 hover:bg-muted/50 focus-within:bg-background focus-within:border-primary/50 focus-within:shadow-sm transition-colors rounded-xl px-4 py-3 cursor-text" onClick={() => (document.querySelector('#msg-input-footer-container .mention-textarea textarea') as HTMLTextAreaElement)?.focus()}>
              <MessageTextArea
                className={cn(
                  "font-normal text-heading-sm text-[color:rgb(var(--text-body))] leading-[20px] w-full min-h-5 bg-transparent border-0 focus:ring-0 p-0 m-0 resize-none hover:border-0",
                  "focus-visible:ring-0 focus-visible:outline-none focus:outline-none"
                )}
                message={message} suggestions={suggestions} onChange={onInputTextChange} onSubmit={submit} />
            </div>
          </div>

          {/* Notify Checkbox */}
          <div className="w-full empty:hidden mt-2">
            {NotifyOnlyMentionedPart()}
          </div>
        </div>

        {/* Bottom Section - Figma: px-6 pb-6 pt-0 justify-between */}
        <div className="flex items-center justify-between pb-4 px-4 relative w-full">
          {/* Left: Plus & Emoji */}
          <div className="flex gap-2 items-center">
            <MsgAddMorePopover room={room} parentMessage={parentMessage?.msg}>
              <Button variant="icon-outline">
                <IconPlus />
              </Button>
            </MsgAddMorePopover>

            <Button
              variant="icon-outline"
              onClick={() => {
                setShowEmojiSelector(true);
                focusTextAreaIfKeyboardIsOpen();
              }}
            >
              <IconMoodSmile />
            </Button>
          </div>

          {/* Right: Record & Send */}
          <div className="flex gap-2 items-center justify-end">
            <MsgRecordModal
              onAction={handleRecordingAction}
              customButton={
                <Button variant="icon-outline">
                  <IconMicrophone />
                </Button>
              }
            />

            <SendButton emitter={sendActiveEmitter} onSubmit={submit} />
          </div>
        </div>

        <MsgEmojiSelector show={showEmojiSelctor} setShow={setShowEmojiSelector} onSelect={handleEmojiSelected} />
      </div>
    </UploadFilesContext.Provider>
  )
  function getFileItem(file: File, index: number) {
    const removeBtn = (
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/80 hover:bg-background shadow-sm z-10"
        onClick={() => removeFile(index, file)}
      >
        <X className="h-4 w-4 text-card-foreground" />
      </Button>
    );
    let fileKey = `${file.name}/${index}`;

    return (
      <div key={index} className="relative inline-flex flex-col shrink-0 bg-background rounded-lg border border-border overflow-hidden">
        {file.type.includes('image') ? (
          <div className="relative h-24 w-24">
            <img src={URL.createObjectURL(file)} title={file.name} className="h-full w-full object-cover" alt={file.name} />
            {removeBtn}
          </div>
        ) : (
          <div className="relative flex flex-col items-center justify-center p-3 min-w-[100px] max-w-[120px]">
            <div className="mb-2">
              <FileIcon filename={file.name} />
            </div>
            {progress[fileKey] && <Progress value={progress[fileKey] * 100} className="absolute bottom-1 left-0 right-0 h-1" />}
            {removeBtn}
            <p className="text-xs text-center break-all line-clamp-2 px-1 text-[color:rgb(var(--text-body))] mt-1">{file.name}</p>
            <p className="text-meta-sm text-muted-foreground">{Util.niceBytes(file.size)}</p>
          </div>
        )}
      </div>
    );
  }

  function removeFile(index: number, file: any) {
    let fileKey = `${file.name}/${index}`;
    setProgress((prevProgress) => {
      const newProgress = { ...prevProgress };
      delete newProgress[fileKey];
      return newProgress;
    });
    if (uploadTask[fileKey] != null) {
      uploadTask[fileKey].cancel();
      setUploadTask((prevUploadTask) => {
        const newUploadTask = { ...prevUploadTask };
        delete newUploadTask[fileKey];
        return newUploadTask;
      });
    }
    setFiles(files.filter(i => i != file));
  }


  function handleRecordingAction(action: RecordingAction, text?: string, recording?: RecordingData) {
    if (action === 'send-text' && text && message) {
      message.text = text;
      if (recording) message.recording = recording;
      forceUpdate();
      submit();
    }
  }

  function ShowRecording() {
    if (message.recording) {
      return (
        <div className="relative border-t border-border pt-2">
          <PlayMessageButton m={new UIMessage(message, [])} />
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-[90px] top-5 h-6 w-6 z-10"
            onClick={clearRecording}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      );
    }
    else
      return <></>
  }

  function ShowEmojis() {
    if (message.emoji) {
      return (
        <div className="relative border-t border-border pt-2">
          <img src={`assets/emoji/${message.emoji}.png`} className="mt-2 ml-2 w-24 rounded-lg" alt="emoji" />
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-[90px] top-5 h-6 w-6 z-10"
            onClick={clearEmoji}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      );
    }
    else
      return <></>
  }

  function AddEmojiButton() {
    return <></>; // We inlined the AddEmojiButton in the new layout structure directly
  }

  function clearRecording() {
    message.recording = undefined;
    forceUpdate();
  }

  function clearEmoji() {
    message.emoji = undefined;
    forceUpdate();
  }

  function onInputTextChange(value: string) {
    hasTextRef.current = value.length > 0;

    // 🔧 ref로 먼저 비교 → 값이 달라질 때만 setState 호출 → 불필요한 렌더 스케줄 자체를 차단
    const newActive = hasTextRef.current || files.length > 0 || items.length > 0 || !!message.recording || !!message.emoji;
    if (newActive !== sendActiveRef.current) {
      sendActiveRef.current = newActive;
      sendActiveEmitter.emit('sendActive', newActive);
    }

    const newMentioning = value.indexOf('@[') > -1;
    if (newMentioning !== isMentioningRef.current) {
      isMentioningRef.current = newMentioning;
      setIsMentioning(newMentioning);
    }
  }

  // 🔧 파일/이모지/녹음 변경 시 sendActive 동기화
  function updateSendActive() {
    const newActive = hasTextRef.current || files.length > 0 || items.length > 0 || !!message.recording || !!message.emoji;
    if (newActive !== sendActiveRef.current) {
      sendActiveRef.current = newActive;
      sendActiveEmitter.emit('sendActive', newActive);
    }
  }

  async function submit() {
    // 첫 번째 댓글인 경우 부모 메시지 존재 여부 확인
    // (이미 댓글이 있으면 부모 삭제가 앱 UI로는 불가능하므로 체크 불필요)
    if (parentMessage && parentMessage.nonReactions().length === 0) {
      const parentExists = await Fire.msg.getMessage(parentMessage.msg.id);
      if (!parentExists) {
        My.DeletedMessages.set(parentMessage.msg.id, true);
        RoomRefreshEmitter.emit(room.id);
        toast({ title: t`This message has been deleted.`, variant: 'destructive', duration: 3000 });
        onClearParent();
        return;
      }
    }

    // 외부 메시지 답글 여부를 onClearParent 전에 미리 평가 (이후 parentMessage가 null이 될 수 있음)
    // webhookUrl 은 스레드 루트(첫 외부 메시지)에만 저장되므로, 답장 대상이 같은 스레드의 후속 메시지여도
    // 루트 기준으로 확인한다. message.parentMessageId 는 위에서 스레드 루트로 정규화됨.
    const webhookRoot = messages.find(m => m.msg.id === message.parentMessageId)?.msg ?? parentMessage?.msg;
    const needsWebhook = !!(message.parentMessageId && webhookRoot?.isExternal && (webhookRoot as any).webhookUrl);

    const msgToSend = message;
    let _room = My.Rooms.get(room.id) ?? room; // Get latest version of the channel.
    msgToSend.userIds = _room.userIds; // Set the latest userIds. Room may gotten new members.
    const uimsg = new UIMessage(msgToSend, []);
    uimsg.sendFiles = files;
    uimsg.isLocal = true;
    hasTextRef.current = false;
    sendActiveRef.current = false;
    sendActiveEmitter.emit('sendActive', false);
    setMessage(new Message(_room, My.user));
    setIsMentioning(false);
    onClearParent(uimsg);

    if (files.length > 0) {
      const fileAttachments = await uploadFilesForMsg(msgToSend.id, files);
      if (fileAttachments !== null) {
        msgToSend.files = fileAttachments;
      } else {
        toast({
          title: t`Failed to send message. Please try again later.`,
        });
        setMessage(msgToSend);
        return;
      }
    }

    if (items.length > 0) {
      items.forEach(item => {
        if (item.url && item.title) {
          const f = new FileAttachment(item.title, item.url, 0, Util.isImageUrl(item.url) ? 'image' : 'doc');
          f.isCopy = true;
          msgToSend.files.push(f);
        }
      });
      setItems([]);
    }

    // 🔧 스레드 답글인 경우, 알림 대상 수집
    // = 부모글 작성자 + 모든 답글 작성자 + 스레드 내에서 멘션된 모든 사용자
    let threadParticipantIds: string[] | undefined;
    if (msgToSend.parentMessageId) {
      const threadParent = messages.find(m => m.msg.id === msgToSend.parentMessageId);
      if (threadParent) {
        const participantSet = new Set<string>();
        // 부모글 작성자
        participantSet.add(threadParent.msg.user.id);
        // 부모글에서 멘션된 사용자
        Util.getMentionedUserIds(threadParent.msg.text).forEach(id => participantSet.add(id));
        // 모든 답글 작성자 + 답글에서 멘션된 사용자 (리액션 제외)
        threadParent.nonReactions().forEach(reply => {
          participantSet.add(reply.msg.user.id);
          Util.getMentionedUserIds(reply.msg.text).forEach(id => participantSet.add(id));
        });
        // 'everyone'은 notifyUsersOnNewMessage에서 별도 처리되므로 제거
        participantSet.delete('everyone');
        threadParticipantIds = Array.from(participantSet);
      }
    }

    try {
      await Fire.msg.saveMessageAndNotify(msgToSend, notifyOnlyMentioned, threadParticipantIds);
      onSendedMessage(uimsg);

      // 외부 메시지 답글이면 즉시 Webhook 전송 (fire-and-forget)
      // callable이 Firestore에 webhookDelivery 기록 → onSnapshot으로 UI 자동 갱신
      // + toast로 즉시 피드백 (스레드 접혀있어도 확인 가능)
      if (needsWebhook) {
        Fire.msg.sendWebhookForMessage(msgToSend.id)
          .then(result => {
            if (result.ok) {
              toast({ title: '✅ Webhook delivered', duration: 3000 });
            } else {
              toast({ title: `❌ Webhook failed${result.error ? `: ${result.error}` : ''}`, variant: 'destructive', duration: 5000 });
            }
          })
          .catch(err => {
            console.error('[Webhook] sendWebhookForMessage failed:', err);
            toast({ title: `❌ Webhook failed: ${err.message || 'Unknown error'}`, variant: 'destructive', duration: 5000 });
          });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: t`Failed to send message. Please try again later.`,
      });
    } finally {
      setInProgress(false);
    }

    Fire.bot.askSoribotIfMentioned(msgToSend, soribotsRef.current ?? [], messages);
  }


  async function uploadFilesForMsg(msgId: string, files: File[]): Promise<FileAttachment[] | null> {
    let fileAttachments: FileAttachment[] = [];
    setFiles([]);
    if (files.length == 0) {
      return fileAttachments;
    } else {
      return new Promise(async (resolve, reject) => {
        for (let index = 0; index < files.length; index++) {
          let file = files[index];
          const filename = (file.name.startsWith('image.')) ? `${index + 1}-${file.name}` : file.name;
          let fileKey = `${msgId}/${file.name}`;
          const group = file.type.includes('image') ? 'image' : 'doc';
          const sendFile = new SendFile(file, room.id);
          sendFile.msgId = msgId;
          sendFile.index = index;
          if (AppSettings.IMAGE_RESIZING_ON_UPLOAD_ENABLED && group == 'image' && file.size > 100000) {
            file = await ResizeImage(file, 1000);
          }

          Fire.uploadFileOptions(
            file,
            `/rooms/${room.id}/${msgId}/${filename}`,
            {
              onTask: (task) => {
                sendFile.uploadTask = task;
                setUploadTask((prevUploadTask) => ({
                  ...prevUploadTask,
                  [fileKey]: task,
                }));
              },
              onComplete: (url) => {
                if (url) {
                  const fileAttachment = new FileAttachment(file.name, url, file.size, group);
                  fileAttachments.push(fileAttachment);
                  if (fileAttachments.length === files.length) {
                    resolve(fileAttachments);
                  }
                } else {
                  resolve(null);
                }
              },
              onProgress: (percent) => {
                sendFile.progress = percent / 100;
                onProgressForFile(fileKey, sendFile);
                setProgress((prevProgress) => ({
                  ...prevProgress,
                  [fileKey]: percent / 100,
                }));
              },
              onStateChange: (state) => {
                switch (state) {
                  case 'paused':
                  case 'running':
                    break;
                }

                files.forEach((f, i) => {
                  if (f === file) {
                    files.splice(i, 1);
                  }
                });
                if (fileAttachments.length === files.length) {
                  resolve(fileAttachments);
                }
              }
            }
          )
        }
      });
    }
  }

  function handleEmojiSelected(emoji: EmojiType) {
    message.emoji = emoji;
    focusTextAreaIfKeyboardIsOpen();
    forceUpdate();
  }

  function getMentionSuggestions(): SuggestionDataItem[] {
    const list: SuggestionDataItem[] = []

    // Add Everyone option
    list.push({ id: 'everyone', display: 'everyone', avatar: '/assets/icon/favicon.png' } as any);

    room.users.forEach(u => {
      list.push({ id: u.id, display: u.name, avatar: u.photoUrl } as any);
    });
    room.bots?.forEach(b => {
      list.push({ id: b.id ?? b.type, display: b.name, avatar: (b as any).photoUrl } as any);
    });
    soribotsRef.current?.filter(b => b.roomIds?.includes(room.id)).forEach(b => {
      list.push({ id: `${b.type}-${b.id}`, display: b.name, avatar: (b as any).photoUrl } as any);
    });
    return list;
  }

  function focusTextAreaIfKeyboardIsOpen() {
    if (isKeyboardOpen) {
      const textarea = document.querySelector('#msg-input-footer-container .mention-textarea textarea') as HTMLTextAreaElement;
      textarea.focus();
    }
  }

  function NotifyOnlyMentionedPart() {
    if (!parentMessage && isMentioning)
      return (
        <div className="flex items-center gap-2 p-2 bg-muted text-sm rounded-lg border border-dashed border-border">
          <Checkbox
            id="notify-mentioned"
            checked={notifyOnlyMentioned}
            onCheckedChange={() => setNotifyOnlyMentioned(p => !p)}
          />
          <label htmlFor="notify-mentioned" className="cursor-pointer select-none">
            <Trans>Notify only mentioned users</Trans>
          </label>
        </div>
      );
    else
      return null;
  }
});

export default MsgInputFooter;

function pasteImageHandler(evt: ClipboardEvent) {
  if (evt.clipboardData) {
    const items: DataTransferItem[] = [].slice.call(evt.clipboardData.items).filter((item: DataTransferItem) => {
      return item.type.indexOf('image') !== -1;
    });

    if (items.length === 0) {
      return;
    } else {
      let file = items[0].getAsFile();
      if (file) {
        MsgInputFooterEmitter.emit('paste', file);
      }
    }
  }
}

document.addEventListener('paste', pasteImageHandler);
